import { useLocalization } from '@lib/localization';
import { useProjectRoute } from '@lib/routing/useProjectRoute';
import type { Inspection } from '@lib/spproject/types';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Box, Chip, Collapse, InputAdornment, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useAppSelector } from '@store/hooks';
import { type FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InspectionDetailPanel from './InspectionDetailPanel';
import {
  EmptyStateBox,
  HeaderIconButton,
  InspectionItem,
  InspectionList,
  OverlayContainer,
  PanelBody,
  PanelCard,
  PanelHeader,
  SearchContainer,
  StyledTextField,
} from './NavOverlay.styled';

const NavOverlay: FC = () => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [collapsed, setCollapsed] = useState<boolean>(false);

  const { t, currentLanguage } = useLocalization();

  const { project, inspection } = useAppSelector((state) => state.viewer);
  const navigate = useNavigate();
  const { buildPath } = useProjectRoute();

  const isDetail = Boolean(inspection);
  const projectId = project?.manifest.projectInfo.id;

  const inspections = useMemo(() => project?.inspections ?? [], [project]);

  const filteredInspections = useMemo(() => {
    if (!searchQuery) return inspections;
    return inspections.filter((item) => item.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [inspections, searchQuery]);

  const openInspection = (inspectionId: string) => {
    if (!projectId) return;
    navigate(buildPath({ inspectionId }));
  };

  const goBack = () => {
    if (!projectId) return;
    navigate(buildPath());
  };

  const formatDate = (date: string | Date) => {
    const value = typeof date === 'string' ? new Date(date) : date;
    return value.toLocaleDateString(currentLanguage, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const renderInspectionItem = (item: Inspection.Info) => (
    <InspectionItem key={item.id} onClick={() => openInspection(item.id)}>
      <ListItemIcon>
        <DescriptionRoundedIcon sx={{ fontSize: 20 }} />
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography
            noWrap
            sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {item.name}
          </Typography>
        }
        secondary={
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
            <AccessTimeRoundedIcon sx={{ fontSize: 12, opacity: 0.6 }} />
            <Typography sx={{ fontSize: 11.5, opacity: 0.75, lineHeight: 1.2 }}>
              {formatDate(item.dateCreated)}
            </Typography>
          </Stack>
        }
        secondaryTypographyProps={{ component: 'div' }}
      />
    </InspectionItem>
  );

  return (
    <OverlayContainer>
      <PanelCard>
        <PanelHeader>
          {isDetail ? (
            <>
              <HeaderIconButton onClick={goBack} aria-label={t('NavOverlay_BackToInspections')} size="small">
                <ArrowBackIosNewRoundedIcon sx={{ fontSize: 15 }} />
              </HeaderIconButton>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontWeight: 600, fontSize: 15, color: 'text.primary', lineHeight: 1.3 }}>
                  {inspection?.inspection.info.name}
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', letterSpacing: '0.02em' }}>
                  {t('Common_Inspections')}
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Chip
                label={inspections.length}
                size="small"
                sx={{
                  height: 24,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  borderRadius: 99,
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.14),
                  color: 'primary.main',
                }}
              />
              <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.01em', flexGrow: 1 }}>
                {t('Common_Inspections')}
              </Typography>
            </>
          )}
          <HeaderIconButton
            onClick={() => setCollapsed((prev) => !prev)}
            aria-label={collapsed ? t('NavOverlay_ExpandPanel') : t('NavOverlay_CollapsePanel')}
            size="small"
          >
            <ExpandMoreRoundedIcon
              sx={{ transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.25s ease' }}
            />
          </HeaderIconButton>
        </PanelHeader>

        <Collapse in={!collapsed} timeout={250}>
          {isDetail && inspection ? (
            <InspectionDetailPanel inspection={inspection} />
          ) : (
            <>
              <SearchContainer>
                <StyledTextField
                  placeholder={t('Project_Inspections_Search')}
                  fullWidth
                  size="small"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.7 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </SearchContainer>

              <PanelBody>
                {filteredInspections.length > 0 ? (
                  <InspectionList>{filteredInspections.map(renderInspectionItem)}</InspectionList>
                ) : (
                  <EmptyStateBox>
                    <SearchRoundedIcon sx={{ fontSize: 40, opacity: 0.2, mb: 1.5 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {t('NavOverlay_NoMatchingInspections')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, opacity: 0.7 }}>
                      {t('NavOverlay_AdjustSearch')}
                    </Typography>
                  </EmptyStateBox>
                )}
              </PanelBody>
            </>
          )}
        </Collapse>
      </PanelCard>
    </OverlayContainer>
  );
};

export default NavOverlay;
